import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Trans } from 'react-i18next';
import type { ElementType } from 'react';
import { EventIcon } from './EventIcon';
import { Row } from './Row';
import { alpha } from '@mui/material/styles';
import type { CommentedEvent } from '../utils/EventTypes';

// Leaves room for the timestamp column on the right
const CONTENT_MAX_WIDTH = '90%';

const getInitial = (name: string) => name.trim().charAt(0).toUpperCase() || '?';

export const CommentedEventRow = ({
  event,
  icon,
  handleClick,
  translationValues,
}: {
  event: CommentedEvent;
  icon: ElementType;
  handleClick: VoidFunction;
  translationValues: {
    author: string;
    documentName: string;
  };
}) => {
  const authorName = translationValues.author;
  const documentName = translationValues.documentName;
  const hasCommentText = Boolean(event.commentText?.trim());
  const iconSlot = (
    <Stack
      sx={{
        flexShrink: 0,
        alignItems: 'center',
        alignSelf: 'stretch',
        my: 2,
      }}
    >
      <EventIcon icon={icon} />
      <Box data-testid="commented-event-icon-connector" sx={{ width: '1px', flex: 1, mt: 1, bgcolor: '#E0E0E0' }} />
    </Stack>
  );

  return (
    <Row datetime={event.createdAt} iconSlot={iconSlot} viewed={event.viewed} handleClick={handleClick} contentAlignItems="flex-start">
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          flex: 1,
          minWidth: 0,
          py: 2,
          maxWidth: CONTENT_MAX_WIDTH,
        }}
      >
        <Stack sx={{ flexShrink: 0 }}>
          <Avatar
            data-testid="commented-event-avatar"
            sx={{
              width: '42px',
              height: '42px',
              color: 'text.primary',
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
              border: 'none',
              fontWeight: 600,
              fontSize: '16px',
            }}
          >
            {getInitial(authorName)}
          </Avatar>
        </Stack>
        <Stack
          direction="column"
          sx={{
            fontSize: '16px',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography variant="body1" sx={{ minWidth: 0, color: 'text.primary', fontSize: '16px' }}>
            <Trans
              i18nKey="notifications_feed.commented_event"
              values={{
                author: authorName,
                documentName,
              }}
            />
          </Typography>
          <Typography data-testid="commented-event-absolute-time" variant="body2" color="text.secondary" sx={{ fontSize: '14px' }}>
            {event.createdAt.format('h:mm a')}
          </Typography>
          {hasCommentText ? (
            <Box
              data-testid="commented-event-quote"
              component="blockquote"
              sx={{
                p: 2,
                borderRadius: '8px',
                border: '1px solid #E6EAEB',
                maxWidth: '400px',
                mt: 0.5,
                mb: 0,
              }}
            >
              <Typography
                data-testid="commented-event-comment-text"
                variant="body1"
                color="text.primary"
                sx={{ whiteSpace: 'pre-wrap', fontSize: '16px' }}
              >
                {event.commentText}
              </Typography>
            </Box>
          ) : null}
        </Stack>
      </Stack>
    </Row>
  );
};
